import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [locationsOpen, setLocationsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [cities, setCities] = useState([]);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const fetchCities = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cities`);
      setCities(response.data);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const services = [
    { id: 'kitchen-remodeling', name: 'Kitchen Remodeling' },
    { id: 'bathroom-remodeling', name: 'Bathroom Remodeling' },
    { id: 'deck-building', name: 'Trex Decking' },
    { id: 'basement-finishing', name: 'Basement Finishing' },
    { id: 'vinyl-siding', name: 'Vinyl Siding' },
    { id: 'window-replacement', name: 'Window Replacement' },
    { id: 'custom-furniture', name: 'Custom Furniture & Aquariums' },
    { id: 'home-maintenance', name: 'Home Maintenance' },
  ];

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-lg' : 'bg-white'
      }`}
    >
      <div className="border-b border-gray-200">
        {/* Top bar */}
        <div className="bg-[#5c0701] text-white py-2">
          <div className="container mx-auto px-4 flex justify-between items-center text-sm">
            <div className="flex items-center gap-4">
              <span>⏰ Mon-Fri: 5:00 AM - 5:30 PM</span>
            </div>
            <a
              href="tel:2036043202"
              className="font-semibold hover:underline"
              data-testid="header-phone"
            >
              📞 (203) 604-3202
            </a>
          </div>
        </div>

        {/* Main navigation */}
        <nav className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo - Text Only */}
            <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
              <div className="text-2xl font-bold">
                <span className="text-[#5c0701]">CT</span>
                <span className="text-[#3a87fe]">Home</span>
                <span className="text-gray-800">Remodels.com</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <Link
                to="/"
                className="text-gray-700 hover:text-[#3a87fe] font-medium transition"
                data-testid="nav-home"
              >
                Home
              </Link>

              {/* Services Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <Link
                  to="/services"
                  className="text-gray-700 hover:text-[#3a87fe] font-medium transition flex items-center gap-1 py-2"
                  data-testid="nav-services"
                >
                  Services
                  <svg className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                    <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                  </svg>
                </Link>
                {servicesOpen && (
                  <div className="absolute top-full left-0 w-64 pt-0">
                    <div className="bg-white shadow-xl rounded-lg py-2 border border-gray-200">
                      {services.map((service) => (
                        <Link
                          key={service.id}
                          to={`/services/${service.id}`}
                          className="block px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-[#3a87fe] transition"
                          data-testid={`nav-service-${service.id}`}
                          onClick={() => setServicesOpen(false)}
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Areas Served Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setLocationsOpen(true)}
                onMouseLeave={() => setLocationsOpen(false)}
              >
                <button
                  className="text-gray-700 hover:text-[#3a87fe] font-medium transition flex items-center gap-1 py-2"
                  data-testid="nav-locations"
                >
                  Areas Served
                  <svg className={`w-4 h-4 transition-transform ${locationsOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                    <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                  </svg>
                </button>
                {locationsOpen && (
                  <div className="absolute top-full left-0 w-72 pt-0">
                    <div className="bg-white shadow-xl rounded-lg py-2 border border-gray-200 max-h-96 overflow-y-auto">
                      {cities.map((city) => (
                        <Link
                          key={city.id}
                          to={`/locations/${city.id}`}
                          className="block px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-[#3a87fe] transition"
                          data-testid={`nav-city-${city.id}`}
                          onClick={() => setLocationsOpen(false)}
                        >
                          {city.name}, {city.state} <span className="text-sm text-gray-500">({city.distance})</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/portfolio"
                className="text-gray-700 hover:text-[#3a87fe] font-medium transition"
                data-testid="nav-portfolio"
              >
                Portfolio
              </Link>
              <Link
                to="/blog"
                className="text-gray-700 hover:text-[#3a87fe] font-medium transition"
                data-testid="nav-blog"
              >
                CT Remodel News
              </Link>
              <Link
                to="/about"
                className="text-gray-700 hover:text-[#3a87fe] font-medium transition"
                data-testid="nav-about"
              >
                About
              </Link>
              <Link
                to="/contact"
                className="bg-[#3a87fe] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#2968d9] transition"
                data-testid="nav-contact-cta"
              >
                Contact Us
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-gray-700"
              data-testid="mobile-menu-button"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
              <Link to="/" className="block py-2 text-gray-700 hover:text-[#3a87fe] font-medium" data-testid="mobile-nav-home" onClick={() => setMobileMenuOpen(false)}>
                Home
              </Link>
              <div className="py-2">
                <Link to="/services" className="block text-gray-700 hover:text-[#3a87fe] font-medium mb-1" data-testid="mobile-nav-services" onClick={() => setMobileMenuOpen(false)}>
                  All Services
                </Link>
                <div className="pl-4 space-y-1">
                  {services.map((service) => (
                    <Link
                      key={service.id}
                      to={`/services/${service.id}`}
                      className="block py-1 text-sm text-gray-600 hover:text-[#3a87fe]"
                      data-testid={`mobile-nav-service-${service.id}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              </div>
              <Link to="/portfolio" className="block py-2 text-gray-700 hover:text-[#3a87fe] font-medium" data-testid="mobile-nav-portfolio" onClick={() => setMobileMenuOpen(false)}>
                Portfolio
              </Link>
              <Link to="/blog" className="block py-2 text-gray-700 hover:text-[#3a87fe] font-medium" data-testid="mobile-nav-blog" onClick={() => setMobileMenuOpen(false)}>
                CT Remodel News
              </Link>
              <Link to="/about" className="block py-2 text-gray-700 hover:text-[#3a87fe] font-medium" data-testid="mobile-nav-about" onClick={() => setMobileMenuOpen(false)}>
                About
              </Link>
              <Link to="/contact" className="block py-2 text-gray-700 hover:text-[#3a87fe] font-medium" data-testid="mobile-nav-contact" onClick={() => setMobileMenuOpen(false)}>
                Contact
              </Link>
              <div className="mt-4">
                <a href="tel:2036043202" className="block bg-[#3a87fe] text-white px-6 py-3 rounded-lg font-semibold text-center" data-testid="mobile-call-button">
                  Call Now: (203) 604-3202
                </a>
              </div>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;